import './index.scss';

import { Column, Columns } from 'Components/Elements/Layouts';
import StickyElement from 'Components/Elements/StickyElement';
import PropUpPage from 'Components/PopUpPage';
import Header from 'Features/Header/Header';
import cls from 'classnames';
import classNames from 'classnames';
import { useSelectLabelForProductBookButton, useTrans } from 'core/hooks';
import { selectActiveProductType } from 'core/redux/features/products/reducers/productTypes';
import { actions as navActions } from 'core/redux/features/shared/nav';
import { AppState, selectDeviceType } from 'core/redux/reducers';
import { LocationUtil, locationParser } from 'core/utils/location';
import { position, querySelectorAll } from 'dom-helpers';
import addCss from 'dom-helpers/css';
import qsa from 'dom-helpers/querySelectorAll';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { useKlaviyoTrackProductView } from '../../../utils/klaviyo';
import { useViewContentEffect } from '../../Shared/hooks';
import BookButton from '../Components/BookButton';
import {
  useScrollToEl,
  useSelectProductBySlug,
  useSelectViewModes,
  useViewProductEffect,
} from '../hooks';
import {
  Description,
  Menus,
  MobileHeader,
  ProductHeadInfo,
} from './Components';
import MobileTopHeader from './Components/MobileTopHeader';
import InternalLinks from './InternalLinks';

export default function ProductPageComponent({
  className,
}: {
  className?: string;
}) {
  //
  // 3rd party hooks
  //
  const location = useLocation();
  // const history = useHistory()

  //
  // selectors
  //
  const trans = useTrans();
  const dispatch = useDispatch();
  const { viewMode, viewModes } = useSelectViewModes();
  const productFromRoute = useSelectProductBySlug();
  const productId = productFromRoute.id;
  const { viewInfo } = useSelector((state: AppState) => ({
    viewInfo: state.products.viewInfo,
  }));

  const deviceType = useSelector(selectDeviceType);

  const { viewType } = useSelector((state: AppState) => ({
    viewType: selectActiveProductType(state),
  }));

  const mainRef = useRef(null);

  // auto scroll down to page in desktop
  const [triggerScroll, setTriggerScroll] = useState(false);
  const defaultRef = useRef(null);
  useScrollToEl(
    [triggerScroll, deviceType !== 'mobile' && defaultRef.current !== null],
    {
      $el: defaultRef.current,
      top: -20,
      delay: 700,
    },
  );

  // select booking button label
  const bookingButtonLabel = useSelectLabelForProductBookButton({
    product: productFromRoute,
  });

  // tell redux that the user is viewing the product info
  // and fetch the data needed
  useViewProductEffect({
    id: productId,
    slug: productFromRoute.slug,
  });

  // facebook pixel event
  useViewContentEffect({
    product: productFromRoute,
  });

  useEffect(() => {
    const elFooter = qsa(document, '.footer');
    if (elFooter.length > 0 && deviceType === 'mobile') {
      addCss(elFooter[0], {
        display: 'none',
      });
    }

    return () => {
      addCss(elFooter[0], {
        display: '',
      });
    };
  }, [deviceType]);

  useKlaviyoTrackProductView(productFromRoute);
  const popupElRef = useRef<HTMLDivElement>();

  const buttonRef = useRef<HTMLDivElement>();
  useEffect(() => {
    setTimeout(() => {
      addCss(buttonRef.current, {
        bottom: '41px',
      });
    }, 700);
  }, []);

  return (
    <PropUpPage
      ref={popupElRef}
      className={className}
      data-testid="productPage"
      hide={!viewInfo && deviceType === 'mobile'}
      id="page-pop-product"
      autoDisableScroll={true}
      whiteBackdrop={true}
      header={
        <MobileTopHeader
          mainRef={mainRef}
          showOnPin={true}
          activeViewModeId={viewMode.id}
        />
      }
      footer={
        <>
          <div className="product_btn_book" ref={buttonRef}>
            <div className="is-flex is-justify-content-space-between is-align-items-center is-fullheight">
              <Link
                onClick={_e => {
                  if (!popupElRef.current) {
                    return;
                  }

                  let find = querySelectorAll(
                    popupElRef.current,
                    '.pop-page-outer',
                  );

                  if (find.length === 0) {
                    return;
                  }

                  setTimeout(() => {
                    let top = 0;
                    const menuEl = querySelectorAll(
                      popupElRef.current,
                      '.info-view-menu',
                    );

                    if (menuEl.length > 0) {
                      const post = position(menuEl[0], popupElRef.current);
                      top = post.top;
                    }

                    find[0].scrollTo(0, top);
                  }, 300);
                }}
                className="button is-uppercase is-red-dark"
                to={(() => {
                  const loc = new LocationUtil(location);
                  loc.setHash(viewMode.id === 'menu' ? 'desc' : 'menu');
                  return loc.toString();
                })()}
              >
                {trans(
                  viewMode.id === 'menu'
                    ? 'product_page.the_restaurant'
                    : 'product_page.menu',
                )}
              </Link>
              <BookButton
                onClick={() => {
                  dispatch(navActions.hideMenu('main', true));
                }}
                linkType={true}
                url={
                  productFromRoute.status !== 'in-stock' ? viewType.route : null
                }
                className={cls(
                  'button is-uppercase',
                  deviceType !== 'mobile' &&
                    productFromRoute?.status !== 'in-stock'
                    ? 'is-grey-dark has-text-white disabled'
                    : 'is-red-dark',
                )}
              >
                {bookingButtonLabel}
              </BookButton>
            </div>
          </div>
        </>
      }
    >
      <Header showBack={true} hideMiniFilter={true} menuId="product-page" />
      <div id="desc" />
      <div id="menu" />
      <div id="location" />
      <div
        ref={defaultRef}
        className="product-content product-content--display"
        id="product-page"
      >
        <MobileHeader mainRef={mainRef} activeViewModeId={viewMode.id} />

        <Columns gap={false}>
          <Column size={['8-desktop', '7-tablet']}>
            <div className="info-view">
              <ProductHeadInfo />

              <div
                data-testid="productPage.desc"
                style={{ display: viewMode.id === 'desc' ? '' : 'none' }}
              >
                <Description />
              </div>
              <div
                data-testid="productPage.menu"
                style={{ display: viewMode.id === 'menu' ? '' : 'none' }}
              >
                <Menus activeKey={viewMode.key} />
              </div>

              <div
                className="product-content__footer"
                style={{ marginTop: '5rem' }}
              >
                <p className="title is-sm mr-b-small has-text-centered">
                  {trans('cut_price')}
                </p>
                {deviceType !== 'mobile' ? (
                  <div className={cls('book-button')}>
                    <BookButton
                      linkType={productFromRoute.status !== 'in-stock'}
                      url={
                        productFromRoute.status !== 'in-stock'
                          ? viewType.route
                          : null
                      }
                      className={classNames(
                        'button is-huge has-icon is-uppercase',
                        deviceType !== 'mobile' &&
                          productFromRoute?.status !== 'in-stock'
                          ? 'is-grey-dark has-text-white disabled'
                          : 'is-red-dark',
                      )}
                    >
                      {bookingButtonLabel}
                      {deviceType !== 'mobile' &&
                      productFromRoute.status !== 'in-stock' ? null : (
                        <span className="eb eb-pr icon is-right" />
                      )}
                    </BookButton>
                  </div>
                ) : null}
              </div>
            </div>
          </Column>

          <Column size={['4-desktop', '5-tablet']}>
            <div
              style={{ display: deviceType === 'mobile' ? 'none' : '' }}
              data-testid="productPage.modeNav"
              className="product-info info-side"
            >
              <StickyElement>
                <div className="br-0 item-menus">
                  <div className="info-nav">
                    {Object.entries(viewModes).map(([key, view]) => {
                      if (['address'].indexOf(view.id) > -1) {
                        return null;
                      }

                      return (
                        <Link
                          onClick={() => {
                            if (
                              deviceType !== 'mobile' &&
                              view.id !== 'gallery'
                            ) {
                              setTriggerScroll(!triggerScroll);
                            }
                          }}
                          key={key}
                          to={locationParser(location)
                            .setHash(view.route === 'desc' ? '' : view.route)
                            .toString()}
                          className={cls(
                            'button',
                            viewMode.id === view.id && 'is-active',
                          )}
                        >
                          {trans(view.transKey)}
                        </Link>
                      );
                    })}

                    <BookButton
                      linkType={deviceType === 'mobile'}
                      className={classNames(
                        'button',
                        productFromRoute?.status !== 'in-stock'
                          ? 'is-fullwidth is-padded is-grey-dark has-text-white disabled'
                          : 'is-red-dark',
                      )}
                    >
                      {bookingButtonLabel}
                    </BookButton>
                  </div>
                </div>
              </StickyElement>
            </div>
          </Column>
        </Columns>

        {viewMode.id !== 'menu' && <InternalLinks />}
      </div>
    </PropUpPage>
  );
}
